import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

/* ===== 上半：从评价维度到落址观测点（5步演化） ===== */
const evolveSteps = [
  { title: '原始评价维度', subtitle: '课程评价表', color: 'primary', icon: 'ri-folders-line' },
  { title: '拆解、赋予标', subtitle: '48 子观测点', color: 'accent', icon: 'ri-scissors-cut-line' },
  { title: '去重·拆分·归并·标注', subtitle: '9 维 · 24 观测点', color: 'primary', icon: 'ri-git-merge-line' },
  { title: '落址观测点', subtitle: '机制域 4', color: 'accent', icon: 'ri-map-pin-2-line' },
  { title: '打分·定档', subtitle: '交叉验证', color: 'accent', icon: 'ri-bar-chart-grouped-line' },
];

/* ===== 下半：观测点处理四步机制 ===== */
const processSteps = [
  { step: 'STEP 01', title: '去重合并', icon: 'ri-stack-line', color: 'primary' },
  { step: 'STEP 02', title: '拆细赋分', icon: 'ri-scissors-cut-line', color: 'accent' },
  { step: 'STEP 03', title: '落址归并', icon: 'ri-map-pin-2-line', color: 'secondary' },
  { step: 'STEP 04', title: '概念标注', icon: 'ri-price-tag-3-line', color: 'accent' },
];

const evolveCardBg: Record<string, string> = {
  primary: 'bg-primary-50/50 backdrop-blur-md border-primary-200/30 group-hover:border-primary-300/60',
  accent: 'bg-accent-50/50 backdrop-blur-md border-accent-200/30 group-hover:border-accent-300/60',
  secondary: 'bg-secondary-50/50 backdrop-blur-md border-secondary-200/30 group-hover:border-secondary-300/60',
};

const evolveIconBg: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-600',
  accent: 'bg-accent-100 text-accent-600',
  secondary: 'bg-secondary-100 text-secondary-600',
};

const evolveSubtitle: Record<string, string> = {
  primary: 'text-primary-600',
  accent: 'text-accent-600',
  secondary: 'text-secondary-600',
};

const processCardBg: Record<string, string> = {
  primary: 'bg-primary-50/60 border-primary-200/40 group-hover:border-primary-400/50',
  accent: 'bg-accent-50/60 border-accent-200/40 group-hover:border-accent-400/50',
  secondary: 'bg-secondary-50/60 border-secondary-200/40 group-hover:border-secondary-400/50',
};

const processIconBg: Record<string, string> = {
  primary: 'bg-primary-100/80 text-primary-600',
  accent: 'bg-accent-100/80 text-accent-600',
  secondary: 'bg-secondary-100/80 text-secondary-600',
};

const processAccent: Record<string, string> = {
  primary: 'text-primary-600',
  accent: 'text-accent-600',
  secondary: 'text-secondary-600',
};

export default function P35BackendCombined({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <div ref={orb1Ref} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-primary-50/15 blur-[160px] pointer-events-none" style={{ transform: `translate(-50%, calc(-50% + ${orb1Y}px))` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 md:py-10 flex flex-col min-h-screen">
        {/* ===== 标题区 ===== */}
        <div ref={titleRef} className="text-center mb-6 md:mb-10 shrink-0">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
            <span className="gold-dot" />
            后台结构
          </span>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            从专家评价表到AI观测点体系
          </h2>
        </div>

        <div className="flex-1 flex flex-col">
          {/* ===== 五步行演化 ===== */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4 md:mb-6 transition-all duration-700 delay-200" style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(12px)' }}>
              <div className="h-px flex-1 bg-background-200/60" />
              <span className="text-lg md:text-xl lg:text-2xl font-body font-semibold text-foreground-950 uppercase tracking-wider whitespace-nowrap">从评价维度到落址观测点</span>
              <div className="h-px flex-1 bg-background-200/60" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 lg:gap-6">
              {evolveSteps.map((s, i) => (
                <div
                  key={i}
                  className="relative group"
                  style={{
                    opacity: titleVisible ? 1 : 0,
                    transform: titleVisible ? 'translateY(0)' : 'translateY(24px)',
                    transition: `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 90}ms, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 90}ms`,
                  }}
                >
                  {i < evolveSteps.length - 1 && (
                    <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center transition-all duration-300 group-hover:-right-2">
                      <div className="w-6 h-6 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center shadow-sm">
                        <i className="ri-arrow-right-line text-primary-400 text-xs group-hover:text-primary-600 transition-colors" />
                      </div>
                    </div>
                  )}
                  <div className={`p-6 md:p-8 lg:p-10 rounded-2xl border transition-all duration-400 ease-out h-full min-h-[220px] md:min-h-[280px] lg:min-h-[320px] flex flex-col hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-100/10 ${evolveCardBg[s.color]}`}>
                    <div className="flex items-center gap-3 mb-4 md:mb-5">
                      <div className={`w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 ease-out group-hover:scale-110 ${evolveIconBg[s.color]}`}>
                        <i className={`${s.icon} text-xl md:text-2xl lg:text-3xl`} />
                      </div>
                      <span className={`text-base md:text-lg lg:text-xl font-label font-bold px-3 py-1 rounded-full transition-all duration-300 group-hover:brightness-95 ${evolveIconBg[s.color]}`}>
                        步骤 {i + 1}
                      </span>
                    </div>
                    <h4 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3 md:mb-4">{s.title}</h4>
                    <p className={`text-lg md:text-xl lg:text-2xl font-body font-bold ${evolveSubtitle[s.color]}`}>{s.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}